import { Alert, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getCompanySettingsApi, updateCompanySettingsApi } from "../api/companyApi";
import FormLayout from "../components/common/FormLayout";
import { TestId } from "../constants/testIds";
import type { RootState } from "../store";
import type translations from "../translations/translations.json";
import { isUserAdmin } from "../types/user";
import { getErrorMessage } from "../utils/errorHandling";
import { toastEmitter } from "../utils/toastEmitter";

type TranslationKeys = keyof typeof translations.en;

const MAX_DAILY_REPORT_LIMIT = 100;

const CompanySettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const canManage = isUserAdmin(user);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limitInput, setLimitInput] = useState("1");
  const [savedLimit, setSavedLimit] = useState(1);

  useEffect(() => {
    if (!canManage) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getCompanySettingsApi()
      .then((company) => {
        if (cancelled) {
          return;
        }
        setLimitInput(String(company.dailyUangJalanReportLimit));
        setSavedLimit(company.dailyUangJalanReportLimit);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getErrorMessage(err, t, "loadCompanySettings"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canManage, t]);

  const handleSave = async () => {
    const parsed = Number(limitInput);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > MAX_DAILY_REPORT_LIMIT) {
      setError(t("companySettings.invalidLimit" as TranslationKeys));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const company = await updateCompanySettingsApi({
        dailyUangJalanReportLimit: parsed,
      });
      setLimitInput(String(company.dailyUangJalanReportLimit));
      setSavedLimit(company.dailyUangJalanReportLimit);
      toastEmitter.emit(t("companySettings.saved" as TranslationKeys), "success");
    } catch (err) {
      const message = getErrorMessage(err, t, "updateCompanySettings");
      setError(message);
      toastEmitter.emit(message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <FormLayout title={t("Company Settings" as TranslationKeys)}>
        <Alert severity="error">{t("companySettings.accessDenied" as TranslationKeys)}</Alert>
      </FormLayout>
    );
  }

  if (loading) {
    return (
      <FormLayout title={t("Company Settings" as TranslationKeys)}>
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress data-testid={TestId.COMMON.LOADING_SPINNER} />
        </Stack>
      </FormLayout>
    );
  }

  return (
    <FormLayout title={t("Company Settings" as TranslationKeys)}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("companySettings.dailyReportLimitHelp" as TranslationKeys)}
      </Typography>
      <Stack spacing={2} direction={{ xs: "column", sm: "row" }} alignItems="flex-start">
        <TextField
          type="number"
          label={t("companySettings.dailyReportLimit" as TranslationKeys)}
          value={limitInput}
          onChange={(event) => setLimitInput(event.target.value)}
          inputProps={{
            min: 1,
            max: MAX_DAILY_REPORT_LIMIT,
            step: 1,
            "data-testid": TestId.COMPANY_SETTINGS.DAILY_REPORT_LIMIT_INPUT,
          }}
          size="small"
        />
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || limitInput === String(savedLimit)}
          data-testid={TestId.COMPANY_SETTINGS.SAVE_BUTTON}
          sx={{ minWidth: 100 }}
        >
          {t("Save" as TranslationKeys)}
        </Button>
      </Stack>
      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}
    </FormLayout>
  );
};

export default CompanySettingsPage;
